import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { ZodType } from "zod";

import { cn } from "@/lib/utils";
import { Filter, ChevronDown, X, Check } from "@/components/icons";
import { cva } from "class-variance-authority";
import {
  SearchField,
  SearchFieldInput,
  SearchFieldClearButton,
} from "@/components/designSystem/SearchField";
import { FilterContext } from "@/components/designSystem/FilterSelect";
import { DropdownScrollArea } from "@/components/designSystem/DropdownScrollArea";

const filterButtonVariants = cva(
  "relative flex items-center justify-center gap-2.5 overflow-hidden rounded-md px-3 py-2 transition-all",
  {
    variants: {
      status: {
        normal: "bg-static-white inset-border-filter-normal",
        selected: "bg-primary-strong/2 inset-outline-border-gradient",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      {
        status: "normal",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
      {
        status: "selected",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
    ],
    defaultVariants: {
      status: "normal",
      disabled: false,
    },
  },
);

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDropDownProps {
  label: string;
  options: FilterOption[];
  /** FilterProvider를 사용하지 않는 controlled 모드의 선택값. */
  value?: string | null;
  /** FilterProvider를 사용하지 않는 controlled 모드의 변경 콜백. */
  onValueChange?: (value: string | null) => void;
  /** FilterProvider context에서 값을 읽고 쓸 store 파라미터 키. */
  paramKey?: string;
  /** context에 기록할 값을 검증하는 스키마. */
  schema?: ZodType;
  showFilterIcon?: boolean;
  showClearButton?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
}

function FilterDropDown({
  label,
  options,
  value: valueProp = null,
  onValueChange: onValueChangeProp,
  paramKey,
  schema,
  showFilterIcon = true,
  showClearButton = true,
  onClear,
  disabled = false,
  className,
  searchPlaceholder = "검색어를 입력하세요...",
  showSearch = true,
}: FilterDropDownProps) {
  const ctx = React.useContext(FilterContext);

  const value =
    paramKey && ctx
      ? ((ctx.filterValues[paramKey] as string | null) ?? null)
      : valueProp;

  const onValueChange = React.useCallback(
    (newValue: string | null) => {
      if (paramKey && ctx) {
        if (!newValue) {
          ctx.setParams(paramKey, null);
        } else if (schema) {
          const parsed = schema.safeParse(newValue);
          ctx.setParams(paramKey, parsed.success ? parsed.data : null);
        } else {
          ctx.setParams(paramKey, newValue);
        }
      }
      onValueChangeProp?.(newValue);
    },
    [paramKey, ctx, schema, onValueChangeProp],
  );

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const isSelected = !!value;

  const filteredOptions = showSearch
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : options;

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
    onClear?.();
    onValueChange?.(null);
  };

  const handleItemClick = (itemValue: string) => {
    if (disabled) return;
    onValueChange?.(itemValue);
    setOpen(false);
  };

  const getSelectedText = () => {
    const selectedOption = options.find((opt) => opt.value === value);
    return selectedOption?.label ?? null;
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearchQuery("");
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            filterButtonVariants({
              status: isSelected && !disabled ? "selected" : "normal",
              disabled,
            }),
            "group",
            className,
          )}
        >
          <div
            className={cn("flex items-center gap-1", disabled && "opacity-40")}
          >
            {showFilterIcon && (
              <Filter
                size={16}
                color={
                  isSelected && !disabled ? "primary-normal" : "labels-primary"
                }
              />
            )}

            <span
              className={cn(
                "typo-label-1-semibold whitespace-nowrap",
                isSelected && !disabled
                  ? "text-primary-normal"
                  : "text-labels-alternative",
              )}
            >
              {label}
            </span>

            {isSelected && (
              <span
                className={cn(
                  "typo-label-1-semibold whitespace-nowrap",
                  disabled ? "text-labels-alternative" : "text-primary-strong",
                )}
              >
                {getSelectedText()}
              </span>
            )}

            {isSelected && showClearButton ? (
              <span
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : handleClearClick}
                onPointerDown={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(false);
                    onClear?.();
                    onValueChange?.(null);
                  }
                }}
                className={cn(
                  "flex items-center justify-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer",
                )}
                aria-label="필터 해제"
              >
                <X size={16} color="labels-alternative" />
              </span>
            ) : (
              <ChevronDown size={16} color="labels-alternative" />
            )}
          </div>

          {!disabled && (
            <div
              className={cn(
                "absolute -inset-px pointer-events-none transition-opacity rounded-md",
                "bg-static-black",
                "opacity-0 group-hover:opacity-[0.02] group-active:opacity-10",
              )}
            />
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            "bg-static-white text-labels-primary",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "z-50 w-60 overflow-hidden rounded-xl",
            "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
          )}
          sideOffset={4}
          align="start"
        >
          {showSearch && (
            <div className="border-b border-lines-solid-normal px-2 py-3">
              <SearchField>
                <SearchFieldInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <SearchFieldClearButton />
              </SearchField>
            </div>
          )}

          {/* 스크롤 영역은 검색 입력 아래에만 둔다 — 스크롤바가 검색 필드를 덮지 않게.
              선택 표시 체크는 디자인대로 우측 유지 */}
          <DropdownScrollArea
            className="max-h-70"
            contentClassName="flex flex-col gap-1 px-2 py-3"
          >
            {filteredOptions.map((option) => {
              const isOptionSelected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleItemClick(option.value)}
                  disabled={disabled}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg h-8 px-2 py-1",
                    "cursor-pointer outline-none select-none",
                    "hover:bg-fills-primary focus:bg-fills-primary",
                    disabled && "pointer-events-none opacity-50",
                  )}
                >
                  <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                    {option.label}
                  </span>
                  {isOptionSelected && (
                    <div className="shrink-0 size-5 flex items-center justify-center">
                      <Check size={20} color="primary-normal" />
                    </div>
                  )}
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="px-2 py-4 text-center typo-body-2-regular text-labels-assistive">
                검색 결과가 없습니다.
              </div>
            )}
          </DropdownScrollArea>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

FilterDropDown.displayName = "FilterDropDown";

export { FilterDropDown };
